import { ApiUtils } from 'api/ApiUtils';
import R from 'ramda';
import radar from 'system/radar';
import { DPP } from 'utilities/DPP/DPP';
import { DomHelper } from 'utils/DomHelper';
import { BackendType } from 'utils/DPP/DPP.types';
import { LogHelper } from 'utils/LogHelper';

import xPageId from 'xPageId';

function logApiResponse({
    group,
    method,
    url,
    loggingHeaders,
    startTime,
    endTime,
    status,
    logResponseTextOnError,
    responseText,
    getResponseHeader,
    customData = {
        aborted: false,
        readyState: 4,
    },
    backendType,
}: {
    group: 'api' | 'storage' | 'openstack-api';
    method: HTTPMethod;
    url: string;
    loggingHeaders: AnyObject;
    startTime: number;
    endTime: number;
    status: number;
    logResponseTextOnError: boolean;
    responseText: string;
    getResponseHeader(name: string): string;
    customData?: {
        aborted: boolean;
        readyState: number;
    };
    backendType?: BackendType;
}) {
    const xReqId = loggingHeaders[ApiUtils.HEADER_KEYS.X_REQ_ID_HEADER];
    const requestDuration = Math.max(0, Math.round(endTime - startTime));

    const metrics = radar(group);

    const shortenedUrl = `${method}-${_shortenUrl(url)}`;
    metrics.add(shortenedUrl + '-cnt');

    metrics.add('http' + status);
    metrics.add(shortenedUrl + '-http' + status);

    metrics.add('dur', requestDuration);
    metrics.add(shortenedUrl + '-dur', requestDuration);
    metrics.param(ApiUtils.HEADER_KEYS.X_REQ_ID_HEADER, xReqId);
    const logData: AnyObject = {
        shortenedUrl,
        method: url,
        type: method,
        status,
        duration: requestDuration,
        endTime: LogHelper.getPerformanceDateString(endTime),
        location: window.location.pathname,
        xReqId,
        serverDate: getResponseHeader('date'),
        'x-host': getResponseHeader('x-host'),
        ...filterLoggingHeaders(loggingHeaders),
        ...customData,
    };

    // TODO: move this header recording to OpenstackRPC
    const OPENSTACK_REQ_ID_FIELD = 'x-openstack-request-id';

    const openStackReqId = getResponseHeader(OPENSTACK_REQ_ID_FIELD);

    if (openStackReqId) {
        logData[OPENSTACK_REQ_ID_FIELD] = openStackReqId;
    }

    if (status >= 400) {
        if (logResponseTextOnError) {
            logData.responseText = _getResponseText(responseText);
        }

        // eslint-disable-next-line no-console
        console[status >= 500 || status === 405 ? 'error' : 'warn'](
            'HTTP',
            status,
            url,
            ApiUtils.HEADER_KEYS.X_REQ_ID_HEADER,
            xReqId,
            'ts',
            Math.round(Date.now() / 1000),
        );
    }

    DPP.sendEvent({
        type: 'request',
        payload: {
            group: group.replace('storage', 's3') as any,
            url,
            target: _shortenUrl(url),
            backend: getBackendService(url, backendType),
            method,
            status,
            duration: requestDuration,
            reqId: xReqId,
            openStackReqId,
            mcsReqId: loggingHeaders[ApiUtils.HEADER_KEYS.X_MCS_REQUEST_ID],
            xPageId,
        },
        error: logData.responseText,
    });

    metrics.kaktam('api', logData); // one file for all groups
    metrics.send();
}

const getBackendService = (
    url: string,
    backendType?: BackendType,
): BackendType => {
    const knownTypes = [
        'keystone',
        'nova',
        'karbor',
        'gnocchi',
        'cinder',
        'heat',
        'barbican',
        'sahara',
        'octavia',
        'flipt',
        'strapi',
        'billing',
        'iam',
    ];

    if (backendType && knownTypes.includes(backendType)) {
        return backendType;
    }

    switch (true) {
        case backendType === 'neutron':
        case url.startsWith('/infra/network'):
            return 'neutron';

        case url.startsWith('/infra/container'):
        case url.startsWith('/infra/kubernetes'):
            return 'magnum';

        case url.startsWith('/infra/database'):
            return 'trove';

        case url.startsWith('/infra/share'):
            return 'manila';

        case url.startsWith('/infra/image'):
            return 'glance';

        case url.includes('/api/s3/'):
        case url.includes('/api/v1/hotbox/'):
        case backendType === 's3':
            return 's3';

        case url.startsWith('/cdn/api/'):
        case url.startsWith('/api/cdn/'):
            return 'cdn';

        case url.startsWith('/infra/monitoring'):
        case url.startsWith('/infra/templater'):
            return 'monitoring';

        case url.startsWith('/grm/api/'):
            return '1cgrm';

        case url.startsWith('/api/queue/'):
            return 'sqs';

        default:
            return 'unknown';
    }
};

const _shortenUrl = (url: string) => {
    const uuidRegex = /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/g;
    const mcsPidRegex = /\bmcs\d+/g;
    const apiVersionRegex = /\/api\/v\d/;
    const randomSuffixRegex = /\?_=\d*$/;

    return url
        .replace(/\/infra\/identity\/.*\/auth$/, 'infra-identity-auth')
        .replace(
            /\/infra\/identity\/.*\/redirect\?logout.*/,
            'infra-identity-logout',
        )
        .replace(
            /\/infra\/container\/magnum\/v1\/secrets\/.*/,
            'infra-container-magnum-v1-secrets',
        )
        .replace(apiVersionRegex, '')
        .replace(randomSuffixRegex, '')
        .replace(uuidRegex, 'uuid')
        .replace(mcsPidRegex, 'pid')
        .replace('%projectId%', 'ospid')
        .replace(/\/\d+/g, '/id')
        .replace(/^\/infra\//, '')
        .replace(/^https:\/\//, '')
        .replace('mcs.st', '');
};

function _getResponseText(responseText) {
    responseText = String(responseText);

    if (responseText.length > 4096) {
        responseText = responseText.slice(0, 4096);
    }

    try {
        responseText = JSON.parse(responseText);
    } catch (reason) {
        responseText = DomHelper.removeHtml(responseText)
            ?.trim()
            .replace(/\s+/g, ' ')
            .replace(/\t+/g, '\t')
            .replace(/[\r\n]+/gm, '\n')
            .slice(0, 1024);
    }

    return responseText;
}

function filterLoggingHeaders(headers: StringMap) {
    const logginHeaderKeys = [
        ApiUtils.HEADER_KEYS.X_EMAIL,
        ApiUtils.HEADER_KEYS.X_MCS_REQUEST_ID,
        ApiUtils.HEADER_KEYS.X_PAGE_ID,
        ApiUtils.HEADER_KEYS.X_REQ_ID_HEADER,
        ApiUtils.HEADER_KEYS.X_TAB_ID,
    ];

    return R.pick(logginHeaderKeys, headers);
}

export const ApiLoggingUtils = {
    logApiResponse,
};
